import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Paper,
  Grid,
  TextField,
  MenuItem,
  FormControlLabel,
  Radio,
  RadioGroup,
  IconButton,
  Typography,
  Divider,
  Collapse,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Undo as UndoIcon,
  AttachFile as AttachFileIcon,
  CloudUpload as CloudUploadIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  DeleteOutline as DeleteOutlineIcon,
  InsertDriveFile as InsertDriveFileIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { getScoreColor } from '../utils/scoreColors';
import { riskAnalyses } from '../services/api';

const THEMATIQUES = [
  'statut_juridique',
  'assurance',
  'conformite',
  'gros_oeuvre',
  'reseaux',
  'patrimoine_protege',
  'environnement',
  'incidents',
  'audits',
  'pmr',
  'incendie',
  'autre'
];

const ITEMS_BY_THEMATIQUE = {
  statut_juridique: [
    'titre_propriete',
    'contentieux_cours',
    'precedent_litige',
    'revendication_recente',
    'autre'
  ],
  assurance: [
    'assurance_multirisques',
    'garantie_decennale',
    'attestation_recente',
    'contrat_renouveler',
    'autre'
  ],
  conformite: [
    'securite_incendie',
    'accessibilite_pmr',
    'normes_electriques',
    'normes_hvac',
    'autre'
  ],
  gros_oeuvre: [
    'murs_facades',
    'charpente_toiture',
    'fondation_sol',
    'escaliers_principal',
    'autre'
  ],
  reseaux: [
    'electricite_reseau',
    'plomberie',
    'chauffage_ventilation',
    'systemes_securite_incendie',
    'autre'
  ],
  patrimoine_protege: [
    'chapelle_classee',
    'facades_protegees',
    'interieur_classe',
    'zones_reglementees',
    'autre'
  ],
  environnement: [
    'humidite_moisissures',
    'pollution_urbaine',
    'risques_gel_chaleur',
    'actions_energetiques',
    'autre'
  ],
  incidents: [
    'infiltration',
    'fissures_surface',
    'peintures_ecaillees',
    'degradation_structurelle',
    'autre'
  ],
  audits: [
    'audit_structure',
    'audit_electrique',
    'audit_environnemental',
    'controles_reglementaires',
    'autre'
  ],
  pmr: [
    'escaliers_etroits',
    'rampes_inexistantes',
    'ascenseur_prevoir',
    'signaletique_adaptee',
    'autre'
  ],
  incendie: [
    'detecteurs_incendie',
    'extincteurs',
    'systemes_alarme',
    'compartimentage',
    'autre'
  ]
};

function RiskAnalysis({ risks, setRisks, userRole, buildings = [], onRiskRemoved }) {
  const { t } = useTranslation();
  const [expandedRisks, setExpandedRisks] = useState({});
  const newRiskRef = useRef(null);
  const [validationDialog, setValidationDialog] = useState({ open: false, riskId: null, status: '' });
  const [validationComment, setValidationComment] = useState('');
  const [riskAttachments, setRiskAttachments] = useState({});

  const canValidate = userRole === 'super_admin' || userRole === 'admin_province';

  const getValidationChipProps = (status) => {
    switch (status) {
      case 'valide': return { label: t('validationStatus.valide'), color: 'success' };
      case 'rejete': return { label: t('validationStatus.rejete'), color: 'error' };
      case 'en_attente':
      default: return { label: t('validationStatus.en_attente'), color: 'warning' };
    }
  };

  const handleOpenValidation = (riskId, status) => {
    setValidationDialog({ open: true, riskId, status });
    setValidationComment('');
  };

  const handleCloseValidation = () => {
    setValidationDialog({ open: false, riskId: null, status: '' });
    setValidationComment('');
  };

  const handleSubmitValidation = async () => {
    try {
      await riskAnalyses.validate(validationDialog.riskId, {
        validation_status: validationDialog.status,
        validation_comment: validationComment
      });
      const updatedRisks = risks.map(r => {
        if (r.id === validationDialog.riskId) {
          return {
            ...r,
            validation_status: validationDialog.status,
            validation_comment: validationComment,
            validated_at: new Date().toISOString()
          };
        }
        return r;
      });
      setRisks(updatedRisks);
      handleCloseValidation();
    } catch (error) {
      console.error('Validation error:', error);
    }
  };

  const handleRemoveValidation = async (riskId) => {
    try {
      await riskAnalyses.validate(riskId, {
        validation_status: 'en_attente',
        validation_comment: ''
      });
      const updatedRisks = risks.map(r => {
        if (r.id === riskId) {
          return {
            ...r,
            validation_status: 'en_attente',
            validation_comment: null,
            validated_by: null,
            validated_at: null,
            validator_first_name: null,
            validator_last_name: null
          };
        }
        return r;
      });
      setRisks(updatedRisks);
    } catch (error) {
      console.error('Remove validation error:', error);
    }
  };

  const loadAttachments = async (riskId) => {
    try {
      const response = await riskAnalyses.getAttachments(riskId);
      setRiskAttachments(prev => ({ ...prev, [riskId]: response.data }));
    } catch (error) {
      console.error('Load attachments error:', error);
    }
  };

  // Charger les pièces jointes pour les risques existants
  React.useEffect(() => {
    risks.forEach(risk => {
      if (risk.id && !riskAttachments[risk.id]) {
        loadAttachments(risk.id);
      }
    });
  }, [risks]);

  const handleFileUpload = async (riskId, file) => {
    try {
      await riskAnalyses.uploadAttachment(riskId, file);
      await loadAttachments(riskId);
    } catch (error) {
      console.error('File upload error:', error);
      alert(t('fileUploadError'));
    }
  };

  const handleFileView = async (riskId, attachment) => {
    try {
      const response = await riskAnalyses.downloadAttachment(riskId, attachment.id);
      const blob = new Blob([response.data], { type: attachment.mime_type });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('File view error:', error);
      alert(t('fileViewError'));
    }
  };

  const handleFileDownload = async (riskId, attachment) => {
    try {
      const response = await riskAnalyses.downloadAttachment(riskId, attachment.id);
      const blob = new Blob([response.data], { type: attachment.mime_type });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.original_name;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('File download error:', error);
      alert(t('fileDownloadError'));
    }
  };

  const handleFileDelete = async (riskId, attachId) => {
    if (!window.confirm(t('confirmDeleteFile'))) return;
    try {
      await riskAnalyses.deleteAttachment(riskId, attachId);
      await loadAttachments(riskId);
    } catch (error) {
      console.error('File delete error:', error);
      alert(t('fileDeleteError'));
    }
  };

  const getAttachmentIcon = (mimeType) => {
    if (mimeType === 'application/pdf') return <PdfIcon color="error" />;
    if (mimeType && mimeType.startsWith('image/')) return <ImageIcon color="primary" />;
    return <InsertDriveFileIcon />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const addRisk = () => {
    const newIndex = risks.length;
    setRisks([...risks, {
      batiment_id: null,
      thematique: '',
      thematique_autre: '',
      item: [],
      item_autre: '',
      description: '',
      existe_risque: true,
      pourquoi: '',
      urgence: false,
      securite: false,
      reglementaire: false,
      financier: false,
      ponderation_geo: 100,
      niveau_gravite: 'modere',
      observations: ''
    }]);

    // Réduire tous les risques existants et ouvrir uniquement le nouveau
    const newExpandedState = {};
    for (let i = 0; i < newIndex; i++) {
      newExpandedState[i] = false;
    }
    newExpandedState[newIndex] = true;
    setExpandedRisks(newExpandedState);

    // Scroller vers le nouveau risque après un court délai pour permettre le rendu
    setTimeout(() => {
      if (newRiskRef.current) {
        newRiskRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
  };

  const removeRisk = (index) => {
    if (onRiskRemoved) onRiskRemoved(risks[index].id);
    setRisks(risks.filter((_, i) => i !== index));
    // Remove the expansion state for this risk
    const newExpandedRisks = { ...expandedRisks };
    delete newExpandedRisks[index];
    setExpandedRisks(newExpandedRisks);
  };

  const updateRisk = (index, field, value) => {
    const newRisks = [...risks];
    newRisks[index] = { ...newRisks[index], [field]: value };

    // Si la thématique change, réinitialiser les items
    if (field === 'thematique') {
      newRisks[index].item = [];
      newRisks[index].item_autre = '';
    }

    // Si "existe_risque" passe à false, réinitialiser les 4 critères à false
    if (field === 'existe_risque' && value === false) {
      newRisks[index].urgence = false;
      newRisks[index].securite = false;
      newRisks[index].reglementaire = false;
      newRisks[index].financier = false;
    }

    setRisks(newRisks);
  };

  const toggleRiskExpansion = (index) => {
    setExpandedRisks({
      ...expandedRisks,
      [index]: !expandedRisks[index]
    });
  };

  const getThematiqueLabel = (risk) => {
    if (!risk.thematique) return '';
    if (risk.thematique === 'autre' && risk.thematique_autre) {
      return risk.thematique_autre;
    }
    return t(`thematiques.${risk.thematique}`, risk.thematique);
  };

  const calculateRiskScore = (risk) => {
    // Compter le nombre de critères à OUI (1 point chacun)
    let critereCount = 0;
    if (risk.urgence) critereCount++;
    if (risk.securite) critereCount++;
    if (risk.reglementaire) critereCount++;
    if (risk.financier) critereCount++;

    // Multiplier par la pondération géographique (en pourcentage)
    const ponderation = parseFloat(risk.ponderation_geo) || 100;
    const score = critereCount * (ponderation / 100);

    return score.toFixed(2);
  };

  return (
    <Box>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={addRisk}
        sx={{ mb: 3 }}
      >
        {t('addRisk')}
      </Button>

      {risks.map((risk, index) => {
        const isExpanded = expandedRisks[index] === true; // Default to collapsed
        const thematiqueLabel = getThematiqueLabel(risk);
        const riskScore = calculateRiskScore(risk);
        const isValidated = risk.validation_status === 'valide';
        const title = thematiqueLabel
          ? `${t('investmentLabel')} #${index + 1} : ${thematiqueLabel}`
          : `${t('investmentLabel')} #${index + 1}`;
        const isLastRisk = index === risks.length - 1;

        return (
          <Paper
            key={index}
            ref={isLastRisk ? newRiskRef : null}
            sx={{ p: 3, mb: 3 }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                <IconButton
                  onClick={() => toggleRiskExpansion(index)}
                  size="small"
                  sx={{
                    transition: 'transform 0.3s',
                    transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'
                  }}
                >
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {title}
                </Typography>
                <Box
                  sx={{
                    ml: 2,
                    px: 2,
                    py: 0.5,
                    borderRadius: 2,
                    bgcolor: getScoreColor(riskScore),
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.9rem'
                  }}
                >
                  {t('score')}: {riskScore}
                </Box>
                {risk.validation_status && (
                  <Chip
                    {...getValidationChipProps(risk.validation_status)}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {canValidate && risk.id && risk.validation_status === 'en_attente' && (
                  <>
                    <IconButton
                      onClick={() => handleOpenValidation(risk.id, 'valide')}
                      sx={{ color: 'success.main' }}
                      size="small"
                    >
                      <CheckCircleIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleOpenValidation(risk.id, 'rejete')}
                      sx={{ color: 'error.main' }}
                      size="small"
                    >
                      <CancelIcon />
                    </IconButton>
                  </>
                )}
                {canValidate && risk.id && (risk.validation_status === 'valide' || risk.validation_status === 'rejete') && (
                  <IconButton
                    onClick={() => handleRemoveValidation(risk.id)}
                    sx={{ color: 'warning.main' }}
                    size="small"
                    title={t('removeValidation')}
                  >
                    <UndoIcon />
                  </IconButton>
                )}
                {risk.validation_status !== 'valide' && (
                  <IconButton color="error" onClick={() => removeRisk(index)}>
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            </Box>

            <Collapse in={isExpanded} timeout="auto">
              <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label={t('scope')}
                value={risk.batiment_id ? String(risk.batiment_id) : 'site'}
                onChange={(e) => updateRisk(index, 'batiment_id', e.target.value === 'site' ? null : parseInt(e.target.value, 10))}
                disabled={isValidated}
                helperText={buildings.length === 0 ? t('entireSite') : undefined}
              >
                <MenuItem value="site">{t('entireSite')}</MenuItem>
                {buildings.map((b) => (
                  <MenuItem key={b.id} value={String(b.id)}>
                    {t('building')} {b.numero_batiment}{b.type_batiment ? ` — ${t(`buildingTypes.${b.type_batiment}`, b.type_batiment)}` : ''}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label={t('theme')}
                value={risk.thematique}
                onChange={(e) => updateRisk(index, 'thematique', e.target.value)}
                disabled={isValidated}
              >
                {THEMATIQUES.map((theme) => (
                  <MenuItem key={theme} value={theme}>
                    {t(`thematiques.${theme}`, theme)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {risk.thematique === 'autre' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('other')}
                  value={risk.thematique_autre}
                  onChange={(e) => updateRisk(index, 'thematique_autre', e.target.value)}
                  disabled={isValidated}
                />
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={isValidated || !risk.thematique || risk.thematique === 'autre'}>
                <InputLabel>{t('item')}</InputLabel>
                <Select
                  multiple
                  value={Array.isArray(risk.item) ? risk.item : []}
                  onChange={(e) => updateRisk(index, 'item', e.target.value)}
                  input={<OutlinedInput label={t('item')} />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={value === 'autre' ? t('riskItems.autre') : t(`riskItems.${value}`, value)}
                          size="small"
                        />
                      ))}
                    </Box>
                  )}
                >
                  {risk.thematique && ITEMS_BY_THEMATIQUE[risk.thematique] &&
                    ITEMS_BY_THEMATIQUE[risk.thematique].map((item) => (
                      <MenuItem key={item} value={item}>
                        {t(`riskItems.${item}`, item)}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            {Array.isArray(risk.item) && risk.item.includes('autre') && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('other')}
                  value={risk.item_autre}
                  onChange={(e) => updateRisk(index, 'item_autre', e.target.value)}
                  disabled={isValidated}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label={t('description')}
                value={risk.description}
                onChange={(e) => updateRisk(index, 'description', e.target.value)}
                disabled={isValidated}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" gutterBottom>{t('riskExists')}</Typography>
              <RadioGroup
                row
                value={risk.existe_risque ? 'true' : 'false'}
                onChange={(e) => updateRisk(index, 'existe_risque', e.target.value === 'true')}
              >
                <FormControlLabel value="true" control={<Radio disabled={isValidated} />} label={t('yes')} />
                <FormControlLabel value="false" control={<Radio disabled={isValidated} />} label={t('no')} />
              </RadioGroup>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label={t('why')}
                value={risk.pourquoi}
                onChange={(e) => updateRisk(index, 'pourquoi', e.target.value)}
                disabled={isValidated}
              />
            </Grid>

            {/* Afficher les champs de risque uniquement si existe_risque est true */}
            {risk.existe_risque && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Typography variant="body2" gutterBottom>{t('urgency')}</Typography>
                  <RadioGroup
                    row
                    value={risk.urgence ? 'true' : 'false'}
                    onChange={(e) => updateRisk(index, 'urgence', e.target.value === 'true')}
                  >
                    <FormControlLabel value="true" control={<Radio disabled={isValidated} />} label={t('yes')} />
                    <FormControlLabel value="false" control={<Radio disabled={isValidated} />} label={t('no')} />
                  </RadioGroup>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Typography variant="body2" gutterBottom>{t('security')}</Typography>
                  <RadioGroup
                    row
                    value={risk.securite ? 'true' : 'false'}
                    onChange={(e) => updateRisk(index, 'securite', e.target.value === 'true')}
                  >
                    <FormControlLabel value="true" control={<Radio disabled={isValidated} />} label={t('yes')} />
                    <FormControlLabel value="false" control={<Radio disabled={isValidated} />} label={t('no')} />
                  </RadioGroup>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Typography variant="body2" gutterBottom>{t('regulatory')}</Typography>
                  <RadioGroup
                    row
                    value={risk.reglementaire ? 'true' : 'false'}
                    onChange={(e) => updateRisk(index, 'reglementaire', e.target.value === 'true')}
                  >
                    <FormControlLabel value="true" control={<Radio disabled={isValidated} />} label={t('yes')} />
                    <FormControlLabel value="false" control={<Radio disabled={isValidated} />} label={t('no')} />
                  </RadioGroup>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Typography variant="body2" gutterBottom>{t('financial')}</Typography>
                  <RadioGroup
                    row
                    value={risk.financier ? 'true' : 'false'}
                    onChange={(e) => updateRisk(index, 'financier', e.target.value === 'true')}
                  >
                    <FormControlLabel value="true" control={<Radio disabled={isValidated} />} label={t('yes')} />
                    <FormControlLabel value="false" control={<Radio disabled={isValidated} />} label={t('no')} />
                  </RadioGroup>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('geoWeighting')}
                    value={risk.ponderation_geo}
                    onChange={(e) => updateRisk(index, 'ponderation_geo', e.target.value)}
                    inputProps={{ min: 80, max: 120 }}
                    disabled={isValidated}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label={t('severityLevel')}
                    value={risk.niveau_gravite}
                    onChange={(e) => updateRisk(index, 'niveau_gravite', e.target.value)}
                    disabled={isValidated}
                  >
                    <MenuItem value="critique">{t('critical')}</MenuItem>
                    <MenuItem value="important">{t('important')}</MenuItem>
                    <MenuItem value="modere">{t('moderate')}</MenuItem>
                    <MenuItem value="faible">{t('low')}</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label={t('observations')}
                    value={risk.observations}
                    onChange={(e) => updateRisk(index, 'observations', e.target.value)}
                    disabled={isValidated}
                  />
                </Grid>
              </>
            )}

            {/* Section de pièces jointes */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                {t('attachedFile')}
              </Typography>

              {/* Liste des fichiers joints */}
              {(riskAttachments[risk.id] || []).length > 0 && (
                <Box sx={{ mb: 2 }}>
                  {(riskAttachments[risk.id] || []).map((att) => (
                    <Paper
                      key={att.id}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        bgcolor: 'grey.50'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getAttachmentIcon(att.mime_type)}
                      </Box>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                          {att.original_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(att.file_size)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleFileView(risk.id, att)}
                          title={t('viewFile')}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleFileDownload(risk.id, att)}
                          title={t('downloadFile')}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleFileDelete(risk.id, att.id)}
                          title={t('deleteFile')}
                          disabled={isValidated}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}

              {/* Bouton d'upload */}
              <Box>
                <input
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,application/pdf"
                  style={{ display: 'none' }}
                  id={`file-upload-${index}`}
                  type="file"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0] && risk.id) {
                      handleFileUpload(risk.id, e.target.files[0]);
                      e.target.value = '';
                    }
                  }}
                  disabled={!risk.id || isValidated}
                />
                <label htmlFor={`file-upload-${index}`}>
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    disabled={!risk.id || isValidated}
                  >
                    {t('uploadFile')}
                  </Button>
                </label>
                {!risk.id && (
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                    {t('saveInvestmentFirst')}
                  </Typography>
                )}
              </Box>
            </Grid>

            {risk.validation_status && risk.validation_status !== 'en_attente' && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{
                    p: 2,
                    borderRadius: 1,
                    bgcolor: risk.validation_status === 'valide' ? 'success.light' : 'error.light',
                    opacity: 0.9
                  }}>
                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                      {risk.validation_status === 'valide' ? t('validationStatus.valide') : t('validationStatus.rejete')}
                    </Typography>
                    {(risk.validator_first_name || risk.validator_last_name) && (
                      <Typography variant="body2" sx={{ color: 'white' }}>
                        {t('validatedBy')}: {risk.validator_first_name} {risk.validator_last_name}
                      </Typography>
                    )}
                    {risk.validated_at && (
                      <Typography variant="body2" sx={{ color: 'white' }}>
                        {t('validatedAt')}: {new Date(risk.validated_at).toLocaleDateString()}
                      </Typography>
                    )}
                    {risk.validation_comment && (
                      <Typography variant="body2" sx={{ color: 'white', mt: 0.5 }}>
                        {t('validationComment')}: {risk.validation_comment}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
            </Collapse>
          </Paper>
        );
      })}

      {risks.length === 0 && (
        <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
          Aucun investissement ajouté. Cliquez sur "Ajouter un investissement" pour commencer.
        </Typography>
      )}

      <Dialog open={validationDialog.open} onClose={handleCloseValidation} maxWidth="sm" fullWidth>
        <DialogTitle>
          {validationDialog.status === 'valide' ? t('validate') : t('reject')}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('validationComment')}
            value={validationComment}
            onChange={(e) => setValidationComment(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseValidation}>{t('cancel')}</Button>
          <Button
            onClick={handleSubmitValidation}
            variant="contained"
            color={validationDialog.status === 'valide' ? 'success' : 'error'}
          >
            {validationDialog.status === 'valide' ? t('validate') : t('reject')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default RiskAnalysis;
